/**
 * Rule 46 Ship 2 — statutory document dispatcher.
 *
 * One small function each per document variant. Every variant generates a
 * self-contained HTML string and pipes it through `_openPrintWindow()` so the
 * blob URL + window.print() plumbing stays in exactly one place.
 *
 *   printInvoice(voucher, ctx)      — Tax Invoice (Rule 46)   [legacy export]
 *   printBillOfSupply(voucher, ctx) — Bill of Supply (Rule 49)
 *   printCreditNote(voucher, ctx)   — Credit Note (Rule 53)
 *   printDebitNote(voucher, ctx)    — Debit Note (Rule 53)
 *   printReceiptVoucher(v, ctx)     — Receipt Voucher (Rule 50)
 *   printRefundVoucher(v, ctx)      — Refund Voucher (Rule 51)
 *   printDeliveryChallan(order,ctx) — Delivery Challan (Rule 55) [order doc]
 *   printProformaInvoice(order,ctx) — Proforma Invoice [non-tax, quotation]
 *
 * `dispatchPrint(voucher, ctx)` picks the right one automatically based on
 * voucher_type + doc_variant + is_advance + refund_against_voucher_no.
 */
import { formatINR } from "@/lib/api";
import { amountInWords } from "@/lib/amountInWords";
import { devWarn } from "@/lib/logger";
import { printInvoice as _printTaxInvoice } from "@/lib/printInvoice";
import { templateCss, resolveLogoUrl } from "@/lib/invoiceTemplates";

// ──────────────────────────────────────────────────────────────────────────
// shared helpers
// ──────────────────────────────────────────────────────────────────────────
export const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

export const fmtAddress = (s) =>
  (s || "")
    .split(/\r?\n/)
    .map((l) => escapeHtml(l.trim()))
    .filter(Boolean)
    .join("<br>");

export const fmtMoney = (n) => formatINR(n || 0);

/** Compact CSS reused by every variant — keeps the visual language
 *  consistent across all 8 documents. */
export const SHARED_CSS = `
  * { box-sizing: border-box; }
  body { font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif; color: #0f172a; padding: 24px 32px; max-width: 900px; margin: 0 auto; font-size: 11.5px; line-height: 1.45; }
  .copy-label { position: fixed; top: 12px; right: 24px; font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.12em; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px double #0f172a; padding-bottom: 14px; }
  .header .supplier h1 { font-size: 20px; margin: 0 0 2px; letter-spacing: -0.01em; }
  .header .supplier .gstin { font-family: 'IBM Plex Mono', monospace; font-size: 11px; color: #1d4ed8; font-weight: 600; }
  .header .supplier .addr { font-size: 11px; color: #475569; max-width: 360px; }
  .header .meta { text-align: right; font-size: 11px; }
  .header .meta .title { font-size: 17px; font-weight: 700; color: #1d4ed8; margin-bottom: 4px; }
  .header .meta .title.warn { color: #b45309; }
  .header .meta .title.danger { color: #b91c1c; }
  .header .meta .num { font-family: 'IBM Plex Mono', monospace; }
  .logo { max-height: 56px; max-width: 140px; object-fit: contain; margin-bottom: 6px; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
  .party-box { border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 12px; background: #f8fafc; }
  .party-box .label { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; margin-bottom: 4px; font-weight: 700; }
  .party-box .name { font-size: 12.5px; font-weight: 600; }
  .party-box .gstin { font-family: 'IBM Plex Mono', monospace; color: #1d4ed8; font-size: 10.5px; margin-top: 2px; }
  .party-box .addr { color: #475569; margin-top: 4px; }
  .pos-bar { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 11px; padding: 8px 12px; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 4px; }
  .pos-bar .lbl { color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
  .pos-bar .val { font-weight: 600; }
  .pos-bar .reverse { color: #b91c1c; font-weight: 700; }
  table.lines { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 10.5px; }
  table.lines th { background: #1e293b; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.05em; border: 1px solid #1e293b; }
  table.lines td { border: 1px solid #cbd5e1; padding: 6px 8px; vertical-align: top; }
  table.lines td.num, table.lines th.num { text-align: right; font-family: 'IBM Plex Mono', monospace; }
  table.lines td.hsn { font-family: 'IBM Plex Mono', monospace; font-size: 10px; }
  table.lines td.strong { font-weight: 700; }
  .totals-grid { margin-top: 6px; display: grid; grid-template-columns: 1.4fr 1fr; gap: 12px; }
  .words { padding: 10px 12px; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 11px; }
  .words .lbl { font-size: 9.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
  .totals { font-family: 'IBM Plex Mono', monospace; font-size: 11px; border: 1px solid #cbd5e1; border-radius: 4px; padding: 8px 12px; }
  .totals .row { display: flex; justify-content: space-between; padding: 3px 0; }
  .totals .grand { border-top: 2px solid #0f172a; margin-top: 6px; padding-top: 6px; font-weight: 700; font-size: 14px; color: #0f172a; }
  table.hsn-summary { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 10px; }
  table.hsn-summary th, table.hsn-summary td { border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; }
  table.hsn-summary th { background: #f1f5f9; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; color: #475569; }
  table.hsn-summary td.num { text-align: right; font-family: 'IBM Plex Mono', monospace; }
  table.hsn-summary td.strong { font-weight: 700; }
  .footer { display: grid; grid-template-columns: 1.4fr 1fr; gap: 12px; margin-top: 18px; }
  .bank { font-size: 10.5px; border: 1px solid #cbd5e1; border-radius: 4px; padding: 8px 12px; background: #f8fafc; }
  .bank .lbl { font-size: 9.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; margin-bottom: 4px; }
  .bank .row { display: flex; gap: 6px; }
  .bank .row .k { color: #475569; min-width: 80px; }
  .bank .row .v { font-family: 'IBM Plex Mono', monospace; }
  .sign { font-size: 10.5px; text-align: center; padding: 8px 12px; border: 1px solid #cbd5e1; border-radius: 4px; }
  .sign .for { font-weight: 600; margin-bottom: 36px; }
  .sign .name { font-weight: 600; }
  .sign .desg { font-size: 10px; color: #475569; }
  .ref { margin-top: 8px; padding: 6px 10px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 4px; font-size: 11px; }
  .reason-block { margin-top: 8px; padding: 8px 12px; background: #fef2f2; border-left: 3px solid #b91c1c; border-radius: 0 4px 4px 0; font-size: 11px; }
  .reason-block .lbl { font-size: 9.5px; color: #b91c1c; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; margin-bottom: 3px; }
  .terms { margin-top: 14px; font-size: 9.5px; color: #475569; padding: 8px 12px; background: #f8fafc; border-radius: 4px; white-space: pre-line; }
  .disclosure { margin-top: 12px; padding: 8px 12px; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 4px; font-size: 10px; color: #9a3412; }
  .non-tax-banner { margin-top: 10px; padding: 10px 14px; background: #f0f9ff; border: 2px dashed #38bdf8; border-radius: 6px; text-align: center; font-size: 11px; color: #075985; font-weight: 600; }
  .non-tax-banner small { display: block; font-weight: 400; color: #0369a1; margin-top: 3px; }
  .footnote { margin-top: 14px; font-size: 9px; color: #94a3b8; text-align: center; }
  @media print {
    body { padding: 16px 20px; }
    .copy-label { position: absolute; }
    @page { size: A4 portrait; margin: 12mm; }
  }
`;

/** Per-line tax breakdown — shared with the legacy Tax Invoice template. */
export const calcLineTaxes = (item, voucher) => {
  const taxable = Number(item.amount || 0);
  const gstRate = Number(item.gst_rate || 0);
  if (gstRate === 0 || !voucher) return { taxable, cgst: 0, sgst: 0, igst: 0, total: taxable };
  const totalTax = taxable * (gstRate / 100);
  if (Number(voucher.igst || 0) > 0 && !voucher.cgst) {
    return { taxable, cgst: 0, sgst: 0, igst: totalTax, total: taxable + totalTax };
  }
  return {
    taxable,
    cgst: totalTax / 2,
    sgst: totalTax / 2,
    igst: 0,
    total: taxable + totalTax,
  };
};

/** Render an address-block pair (Bill To / Ship To). */
export const partiesBlock = (v, p) => {
  const billTo = (v.bill_to_address || p.address || "").trim();
  const shipTo = (v.ship_to_address || p.shipping_address || "").trim();
  const showShipBlock = shipTo && shipTo !== billTo;
  return `
<div class="parties">
  <div class="party-box">
    <div class="label">Bill To</div>
    <div class="name">${escapeHtml(v.party_ledger_name || p.name || "—")}</div>
    ${billTo ? `<div class="addr">${fmtAddress(billTo)}</div>` : ""}
    ${p.gstin ? `<div class="gstin">GSTIN ${escapeHtml(p.gstin)}</div>` : `<div class="gstin" style="color:#94a3b8">Unregistered</div>`}
    ${p.contact_person || p.contact || p.email ? `<div class="addr" style="margin-top:3px;font-size:10.5px">${[p.contact_person, p.contact, p.email].filter(Boolean).map(escapeHtml).join(" • ")}</div>` : ""}
  </div>
  ${
    showShipBlock
      ? `<div class="party-box">
           <div class="label">Ship To</div>
           <div class="name">${escapeHtml(v.party_ledger_name || p.name || "—")}</div>
           <div class="addr">${fmtAddress(shipTo)}</div>
         </div>`
      : `<div class="party-box" style="background:#fafafa">
           <div class="label">Ship To</div>
           <div class="addr" style="color:#94a3b8;font-style:italic">Same as billing address</div>
         </div>`
  }
</div>`;
};

/** Supplier (company) header — common across all 8 docs. */
export const supplierHeader = (c, titleText, titleTone = "default", subLabel = "") => `
<div class="header">
  <div class="supplier">
    ${c.logo_url ? `<img src="${escapeHtml(resolveLogoUrl(c.logo_url))}" class="logo" alt="logo"/>` : ""}
    <h1>${escapeHtml(c.name || "—")}</h1>
    ${c.address ? `<div class="addr">${fmtAddress(c.address)}</div>` : ""}
    ${c.phone || c.email ? `<div class="addr" style="margin-top:3px">${[c.phone, c.email].filter(Boolean).map(escapeHtml).join(" • ")}</div>` : ""}
    ${c.gstin ? `<div class="gstin" style="margin-top:4px">GSTIN ${escapeHtml(c.gstin)}${c.pan ? ` · PAN ${escapeHtml(c.pan)}` : ""}</div>` : ""}
  </div>
  <div class="meta">
    <div class="title ${titleTone === "warn" ? "warn" : titleTone === "danger" ? "danger" : ""}">${escapeHtml(titleText)}</div>
    ${subLabel ? `<div style="font-size:9.5px;color:#64748b;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px">${escapeHtml(subLabel)}</div>` : ""}
  </div>
</div>`;

export const signatoryBlock = (c) => `
  <div class="sign">
    <div class="for">For <strong>${escapeHtml(c.name || "—")}</strong></div>
    <div class="name">${escapeHtml(c.signatory_name || "")}</div>
    <div class="desg">${escapeHtml(c.signatory_designation || "Authorised Signatory")}</div>
  </div>`;

export const docMetaInline = (v) => `
  <div style="margin-top:6px">
    <div><span style="color:#64748b">No: </span><span class="num"><strong>${escapeHtml(v.voucher_no || v.order_no || "—")}</strong></span></div>
    <div><span style="color:#64748b">Date: </span><span class="num">${escapeHtml(v.date || "")}</span></div>
    ${v.reference ? `<div><span style="color:#64748b">Ref: </span><span class="num">${escapeHtml(v.reference)}</span></div>` : ""}
  </div>`;

/** Open the print window with a controlled blob URL. */
function _openPrintWindow(html, autoPrint = true) {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    URL.revokeObjectURL(url);
    devWarn("printWindow: popup blocked");
    return;
  }
  win.addEventListener("load", () => {
    try {
      if (autoPrint) win.print();
    } catch (err) {
      devWarn("Print failed", err);
    }
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
}

/** Wraps any inner HTML into a complete document with shared CSS.
 *  ``template`` is the company's ``invoice_template`` — appends the matching
 *  override stylesheet at the end so it wins specificity.
 */
function _wrapDoc(title, copyLabel, bodyHtml, template = "modern") {
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>${SHARED_CSS}
/* Ship 3 — template override */
${templateCss(template)}
</style></head><body>
${copyLabel ? `<div class="copy-label">${escapeHtml(copyLabel)}</div>` : ""}
${bodyHtml}
</body></html>`;
}

// ──────────────────────────────────────────────────────────────────────────
// 1. Tax Invoice (Rule 46) — re-exports the legacy implementation from
//    `printInvoice.js` so existing callers stay backward-compatible.
// ──────────────────────────────────────────────────────────────────────────
export const printInvoice = _printTaxInvoice;

// ──────────────────────────────────────────────────────────────────────────
// 2. Bill of Supply (Rule 49) — no GST columns, mandatory disclosure
// ──────────────────────────────────────────────────────────────────────────
export function printBillOfSupply(voucher, { company = {}, partyLedger = null, copy } = {}) {
  if (!voucher) return;
  const v = voucher;
  const c = company || {};
  const p = partyLedger || {};
  const items = Array.isArray(v.items) ? v.items : [];

  const lineRows = items.map((i, idx) => `
    <tr>
      <td class="num">${idx + 1}</td>
      <td>${escapeHtml(i.item_name)}</td>
      <td class="hsn">${escapeHtml(i.hsn || "—")}</td>
      <td class="num">${escapeHtml(String(i.qty))} ${escapeHtml(i.unit || "")}</td>
      <td class="num">${fmtMoney(i.rate)}</td>
      <td class="num strong">${fmtMoney(i.amount)}</td>
    </tr>`).join("");

  const sub = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const grand = Number(v.total || sub);

  const body = `
${supplierHeader(c, "Bill of Supply", "warn", "Rule 49 of the CGST Rules, 2017")}
<div style="text-align:right;margin-top:4px">${docMetaInline(v)}</div>
${partiesBlock(v, p)}

<div class="non-tax-banner">
  Composition Taxable Person / Exempt Supply — Not eligible to collect tax on supplies
  <small>Issued under Section 31(3)(c) of the CGST Act, 2017</small>
</div>

${items.length ? `
<table class="lines">
  <thead><tr>
    <th>#</th><th>Description of goods / services</th><th>HSN/SAC</th>
    <th class="num">Qty</th><th class="num">Rate</th><th class="num">Amount</th>
  </tr></thead>
  <tbody>${lineRows}</tbody>
</table>` : ""}

<div class="totals-grid">
  <div class="words">
    <div class="lbl">Amount in words</div>
    <div>${escapeHtml(amountInWords(grand))}</div>
  </div>
  <div class="totals">
    <div class="row"><span>Sub-total</span><span>${fmtMoney(sub)}</span></div>
    <div class="row grand"><span>Grand Total</span><span>${fmtMoney(grand)}</span></div>
  </div>
</div>

<div class="footer">
  <div class="bank">
    <div class="lbl">Pay us at</div>
    ${c.email ? `<div class="row"><span class="k">Queries</span><span class="v">${escapeHtml(c.email)}</span></div>` : ""}
    ${c.default_upi_vpa ? `<div class="row"><span class="k">UPI</span><span class="v">${escapeHtml(c.default_upi_vpa)}</span></div>` : ""}
  </div>
  ${signatoryBlock(c)}
</div>

${v.narration ? `<div class="terms"><strong>Narration: </strong>${escapeHtml(v.narration)}</div>` : ""}
${c.default_terms ? `<div class="terms"><strong>Terms &amp; Conditions:</strong>${"\n" + escapeHtml(c.default_terms)}</div>` : ""}

<div class="footnote">Bill of Supply prepared per Rule 49 of the CGST Rules, 2017. Generated by AXIORA.</div>`;

  _openPrintWindow(_wrapDoc(v.voucher_no || "Bill of Supply", copy || "Original for Recipient", body, company.invoice_template));
}

// ──────────────────────────────────────────────────────────────────────────
// 3 & 4. Credit Note / Debit Note (Rule 53) — uses the existing printInvoice
//   path which already handles these voucher_types, but layered with a
//   required "Reason for issue" block per Section 34.
// ──────────────────────────────────────────────────────────────────────────
export function printCreditDebitNote(voucher, ctx = {}) {
  if (!voucher) return;
  // The legacy printInvoice already renders Credit Note / Debit Note headings.
  // We just need to ensure the `reason` is surfaced. Since adding it directly
  // to printInvoice.js would require an intrusive rewrite, we use a small
  // override: inject the reason as a narration prefix so it renders in the
  // existing template's narration block.
  let v = voucher;
  if (voucher.reason && !voucher.narration?.includes("Reason:")) {
    v = {
      ...voucher,
      narration: `Reason: ${voucher.reason}\n${voucher.narration || ""}`.trim(),
    };
  }
  _printTaxInvoice(v, ctx);
}

// ──────────────────────────────────────────────────────────────────────────
// 5. Receipt Voucher (Rule 50) — issued on advance receipt
// ──────────────────────────────────────────────────────────────────────────
export function printReceiptVoucher(voucher, { company = {}, partyLedger = null, copy } = {}) {
  if (!voucher) return;
  const v = voucher;
  const c = company || {};
  const p = partyLedger || {};
  const amount = Number(v.total || 0);
  const advanceTax = Number(v.cgst || 0) + Number(v.sgst || 0) + Number(v.igst || 0);

  const body = `
${supplierHeader(c, "Receipt Voucher", "default", v.is_advance ? "Advance Receipt — Rule 50 CGST Rules" : "Rule 50 of the CGST Rules, 2017")}
<div style="text-align:right;margin-top:4px">${docMetaInline(v)}</div>
${partiesBlock(v, p)}

${v.is_advance ? `
<div class="non-tax-banner" style="background:#fefce8;border-color:#facc15;color:#854d0e">
  Advance Receipt — Tax to be discharged on this advance per Section 31(3)(d) of the CGST Act
</div>` : ""}

<table class="lines">
  <thead><tr>
    <th>Description</th>
    <th class="num">Amount</th>
  </tr></thead>
  <tbody>
    <tr>
      <td>${escapeHtml(v.narration || "Amount received on account")}</td>
      <td class="num strong">${fmtMoney(amount - advanceTax)}</td>
    </tr>
    ${advanceTax > 0 ? `
    <tr><td>Add: CGST</td><td class="num">${fmtMoney(v.cgst || 0)}</td></tr>
    <tr><td>Add: SGST</td><td class="num">${fmtMoney(v.sgst || 0)}</td></tr>
    <tr><td>Add: IGST</td><td class="num">${fmtMoney(v.igst || 0)}</td></tr>` : ""}
  </tbody>
</table>

<div class="totals-grid">
  <div class="words">
    <div class="lbl">Amount received in words</div>
    <div>${escapeHtml(amountInWords(amount))}</div>
  </div>
  <div class="totals">
    <div class="row grand"><span>Total Received</span><span>${fmtMoney(amount)}</span></div>
  </div>
</div>

${v.cheque_details ? `
<div class="bank" style="margin-top:14px">
  <div class="lbl">Mode of Receipt</div>
  <div class="row"><span class="k">Cheque No</span><span class="v">${escapeHtml(v.cheque_details.cheque_no || "—")}</span></div>
  <div class="row"><span class="k">Bank</span><span class="v">${escapeHtml(v.cheque_details.bank_name || "—")}</span></div>
  <div class="row"><span class="k">Dated</span><span class="v">${escapeHtml(v.cheque_details.issue_date || v.date)}</span></div>
</div>` : ""}

<div class="footer">
  <div class="bank">
    <div class="lbl">Place of Supply</div>
    <div class="row"><span class="v">${escapeHtml(v.place_of_supply || "—")} ${v.place_of_supply_state_code ? "(" + escapeHtml(v.place_of_supply_state_code) + ")" : ""}</span></div>
  </div>
  ${signatoryBlock(c)}
</div>

<div class="footnote">Receipt Voucher prepared per Rule 50 of the CGST Rules, 2017. Generated by AXIORA.</div>`;

  _openPrintWindow(_wrapDoc(v.voucher_no || "Receipt Voucher", copy || "Original for Recipient", body, company.invoice_template));
}

// ──────────────────────────────────────────────────────────────────────────
// 6. Refund Voucher (Rule 51) — issued when refunding a prior advance
// ──────────────────────────────────────────────────────────────────────────
export function printRefundVoucher(voucher, { company = {}, partyLedger = null, copy } = {}) {
  if (!voucher) return;
  const v = voucher;
  const c = company || {};
  const p = partyLedger || {};
  const amount = Number(v.total || 0);

  const body = `
${supplierHeader(c, "Refund Voucher", "danger", "Rule 51 of the CGST Rules, 2017")}
<div style="text-align:right;margin-top:4px">${docMetaInline(v)}</div>

${v.refund_against_voucher_no ? `
<div class="ref">
  Refund against Receipt Voucher: <strong>${escapeHtml(v.refund_against_voucher_no)}</strong>
</div>` : ""}

${partiesBlock(v, p)}

<table class="lines">
  <thead><tr>
    <th>Description</th>
    <th class="num">Amount Refunded</th>
  </tr></thead>
  <tbody>
    <tr>
      <td>${escapeHtml(v.narration || v.reason || "Refund of advance — supply not made")}</td>
      <td class="num strong">${fmtMoney(amount)}</td>
    </tr>
  </tbody>
</table>

<div class="totals-grid">
  <div class="words">
    <div class="lbl">Amount refunded in words</div>
    <div>${escapeHtml(amountInWords(amount))}</div>
  </div>
  <div class="totals">
    <div class="row grand"><span>Total Refunded</span><span>${fmtMoney(amount)}</span></div>
  </div>
</div>

${v.reason ? `
<div class="reason-block">
  <div class="lbl">Reason for Refund</div>
  <div>${escapeHtml(v.reason)}</div>
</div>` : ""}

<div class="footer">
  <div class="bank">
    <div class="lbl">Notes</div>
    <div style="font-size:10.5px;color:#475569">Where a Receipt Voucher was earlier issued for an advance and no supply has been made / no Tax Invoice is to be raised, this Refund Voucher must be issued (Rule 51).</div>
  </div>
  ${signatoryBlock(c)}
</div>

<div class="footnote">Refund Voucher prepared per Rule 51 of the CGST Rules, 2017. Generated by AXIORA.</div>`;

  _openPrintWindow(_wrapDoc(v.voucher_no || "Refund Voucher", copy || "Original for Recipient", body, company.invoice_template));
}

// ──────────────────────────────────────────────────────────────────────────
// 7. Delivery Challan (Rule 55) — from an Order doc, not a posting voucher
// ──────────────────────────────────────────────────────────────────────────
export function printDeliveryChallan(order, { company = {}, partyLedger = null, copy } = {}) {
  if (!order) return;
  const o = order;
  const c = company || {};
  const p = partyLedger || {};
  const items = Array.isArray(o.items) ? o.items : [];

  const lineRows = items.map((i, idx) => `
    <tr>
      <td class="num">${idx + 1}</td>
      <td>${escapeHtml(i.item_name)}</td>
      <td class="hsn">${escapeHtml(i.hsn || "—")}</td>
      <td class="num">${escapeHtml(String(i.qty))} ${escapeHtml(i.unit || "")}</td>
      <td class="num">${fmtMoney(i.rate)}</td>
      <td class="num strong">${fmtMoney(i.amount)}</td>
    </tr>`).join("");

  const sub = items.reduce((s, i) => s + Number(i.amount || 0), 0);

  // Synthesise the parties block from order fields
  const vouchProxy = { party_ledger_name: o.party_name, bill_to_address: p.address, ship_to_address: p.shipping_address };

  const body = `
${supplierHeader(c, "Delivery Challan", "warn", "Rule 55 of the CGST Rules, 2017")}
<div style="text-align:right;margin-top:4px">${docMetaInline(o)}</div>
${partiesBlock(vouchProxy, p)}

<div class="non-tax-banner" style="background:#fef3c7;border-color:#f59e0b;color:#92400e">
  This is a Delivery Challan for movement of goods — NOT a Tax Invoice.
  <small>Issued under Rule 55(1) — supply not yet known / job work / liquid gas / SKD-CKD / other</small>
</div>

<table class="lines">
  <thead><tr>
    <th>#</th><th>Description of goods</th><th>HSN/SAC</th>
    <th class="num">Qty</th><th class="num">Value (₹)</th><th class="num">Sub-total</th>
  </tr></thead>
  <tbody>${lineRows}</tbody>
</table>

<div class="totals-grid">
  <div class="words">
    <div class="lbl">Approximate value in words</div>
    <div>${escapeHtml(amountInWords(sub))}</div>
  </div>
  <div class="totals">
    <div class="row grand"><span>Approximate Value</span><span>${fmtMoney(sub)}</span></div>
  </div>
</div>

<div class="disclosure">
  <strong>Note:</strong> Goods are not for sale unless invoice is separately issued. Place of supply, transporter details and vehicle number must be filled in by the consignor at the time of dispatch (Rule 55(2)).
</div>

<div class="footer">
  <div class="bank">
    <div class="lbl">Transport Details</div>
    <div class="row"><span class="k">Vehicle No</span><span class="v">__________________</span></div>
    <div class="row"><span class="k">Driver Name</span><span class="v">__________________</span></div>
    <div class="row"><span class="k">Signature</span><span class="v">__________________</span></div>
  </div>
  ${signatoryBlock(c)}
</div>

${o.narration ? `<div class="terms"><strong>Narration: </strong>${escapeHtml(o.narration)}</div>` : ""}

<div class="footnote">Delivery Challan prepared per Rule 55 of the CGST Rules, 2017. Generated by AXIORA.</div>`;

  _openPrintWindow(_wrapDoc(o.order_no || "Delivery Challan", copy || "Original for Recipient", body, company.invoice_template));
}

// ──────────────────────────────────────────────────────────────────────────
// 8. Proforma Invoice — commercial quote (NOT a tax document)
// ──────────────────────────────────────────────────────────────────────────
export function printProformaInvoice(order, { company = {}, partyLedger = null, copy } = {}) {
  if (!order) return;
  const o = order;
  const c = company || {};
  const p = partyLedger || {};
  const items = Array.isArray(o.items) ? o.items : [];

  const lineRows = items.map((i, idx) => {
    const t = calcLineTaxes(i, o);
    const gstPct = Number(i.gst_rate || 0);
    return `
    <tr>
      <td class="num">${idx + 1}</td>
      <td>${escapeHtml(i.item_name)}</td>
      <td class="hsn">${escapeHtml(i.hsn || "—")}</td>
      <td class="num">${escapeHtml(String(i.qty))} ${escapeHtml(i.unit || "")}</td>
      <td class="num">${fmtMoney(i.rate)}</td>
      <td class="num">${gstPct ? gstPct.toFixed(1) + "%" : "—"}</td>
      <td class="num strong">${fmtMoney(t.total)}</td>
    </tr>`;
  }).join("");

  const sub = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalTax = items.reduce((s, i) => s + (calcLineTaxes(i, o).cgst + calcLineTaxes(i, o).sgst + calcLineTaxes(i, o).igst), 0);
  const grand = sub + totalTax;

  const vouchProxy = { party_ledger_name: o.party_name, bill_to_address: p.address, ship_to_address: p.shipping_address };

  const body = `
${supplierHeader(c, "Proforma Invoice", "default", "Commercial Quotation — Not a Tax Invoice")}
<div style="text-align:right;margin-top:4px">${docMetaInline(o)}</div>
${partiesBlock(vouchProxy, p)}

<div class="non-tax-banner">
  This is a Proforma Invoice / Estimate — NOT a Tax Invoice.
  <small>No GST is charged at this stage. A formal Tax Invoice will be raised on confirmation of order.</small>
</div>

<table class="lines">
  <thead><tr>
    <th>#</th><th>Description of goods / services</th><th>HSN/SAC</th>
    <th class="num">Qty</th><th class="num">Rate</th>
    <th class="num">GST %</th><th class="num">Indicative Total</th>
  </tr></thead>
  <tbody>${lineRows}</tbody>
</table>

<div class="totals-grid">
  <div class="words">
    <div class="lbl">Indicative total in words</div>
    <div>${escapeHtml(amountInWords(grand))}</div>
  </div>
  <div class="totals">
    <div class="row"><span>Sub-total (taxable)</span><span>${fmtMoney(sub)}</span></div>
    ${totalTax ? `<div class="row"><span>Estimated GST</span><span>${fmtMoney(totalTax)}</span></div>` : ""}
    <div class="row grand"><span>Indicative Total</span><span>${fmtMoney(grand)}</span></div>
  </div>
</div>

<div class="disclosure">
  <strong>Validity:</strong> This quotation is valid for 30 days from the date of issue unless otherwise specified. Prices are subject to change without notice. Goods/services will be supplied only against a confirmed Purchase Order and a separately issued Tax Invoice.
</div>

<div class="footer">
  <div class="bank">
    <div class="lbl">For queries</div>
    ${c.email ? `<div class="row"><span class="k">Email</span><span class="v">${escapeHtml(c.email)}</span></div>` : ""}
    ${c.phone ? `<div class="row"><span class="k">Phone</span><span class="v">${escapeHtml(c.phone)}</span></div>` : ""}
    ${c.default_upi_vpa ? `<div class="row"><span class="k">UPI</span><span class="v">${escapeHtml(c.default_upi_vpa)}</span></div>` : ""}
  </div>
  ${signatoryBlock(c)}
</div>

${o.narration ? `<div class="terms"><strong>Notes: </strong>${escapeHtml(o.narration)}</div>` : ""}
${c.default_terms ? `<div class="terms"><strong>Terms &amp; Conditions:</strong>${"\n" + escapeHtml(c.default_terms)}</div>` : ""}

<div class="footnote">Proforma Invoice generated by AXIORA. For statutory compliance purposes, the corresponding Tax Invoice (Rule 46) takes precedence.</div>`;

  _openPrintWindow(_wrapDoc(o.order_no || "Proforma Invoice", copy || "Original for Recipient", body, company.invoice_template));
}

// ──────────────────────────────────────────────────────────────────────────
// Dispatcher — pick the right printer for a voucher.
// Callers (Vouchers list, voucher detail) should use this entry point so the
// statutory heading switches automatically.
// ──────────────────────────────────────────────────────────────────────────
export function dispatchPrintVoucher(voucher, ctx = {}) {
  if (!voucher) return;
  const t = voucher.voucher_type;
  const variant = voucher.doc_variant || "";

  if (t === "Sales" && variant === "bill_of_supply") {
    return printBillOfSupply(voucher, ctx);
  }
  if (t === "Credit Note" || t === "Debit Note") {
    return printCreditDebitNote(voucher, ctx);
  }
  if (t === "Receipt") {
    return printReceiptVoucher(voucher, ctx);
  }
  if (t === "Payment" && voucher.refund_against_voucher_no) {
    return printRefundVoucher(voucher, ctx);
  }
  // Default — tax invoice template (handles Sales, Purchase, fallback)
  return _printTaxInvoice(voucher, ctx);
}

/** Convenience dispatcher for orders module. */
export function dispatchPrintOrder(order, ctx = {}) {
  if (!order) return;
  const t = order.order_type;
  if (t === "Quotation") return printProformaInvoice(order, ctx);
  if (t === "Delivery Note") return printDeliveryChallan(order, ctx);
  // Sales Order / Purchase Order / Goods Receipt — use proforma layout as
  // a generic non-tax document; the heading just changes.
  return printProformaInvoice(order, ctx);
}
